/**
 * Kpop Spotlight — the centred player for video cards (archive tiles and the
 * photocard rail).
 *
 * The card's picture lifts out and flies into a centred 16:9 player while the
 * page dims behind it; an equalizer plays until YouTube is ready. Esc, "Sulje"
 * or a click outside flies it back into the card, and focus returns there (the
 * dialog does that natively). Cards stay links to YouTube: modified clicks
 * still open a new tab, and without JS they are just links.
 */

import { EASE, embed, initStillFallbacks, isPlainClick, px, reducedMotion, staggerIn, unembed } from './player.js';

let dialog;
let frame;
let strip;
let source = null;
let busy = false;

function build() {
	dialog = document.createElement('dialog');
	dialog.className = 'spot-player';
	dialog.setAttribute('aria-label', 'Video');
	dialog.innerHTML =
		'<div class="spot-player__scrim"></div><div class="spot-player__box"><div class="spot-player__frame spot-media"></div><div class="spot-player__strip"></div></div>';
	frame = dialog.querySelector('.spot-player__frame');
	strip = dialog.querySelector('.spot-player__strip');

	dialog.addEventListener('cancel', (e) => {
		e.preventDefault(); // Esc animates out too
		close();
	});
	dialog.addEventListener('click', (e) => {
		if (e.target.closest('.spot-player__scrim, [data-spot-player-close]')) close();
	});
	// Browsers may close a modal dialog on their own (a second quick Esc cannot
	// be cancelled), so the cleanup lives on the close event, not only in close().
	dialog.addEventListener('close', reset);
	document.body.append(dialog);
}

/** A copy of el pinned over the viewport at rect, for the flight. */
function ghostOf(el, rect, radius) {
	const g = el.cloneNode(true);
	g.querySelectorAll('iframe, .spot-eq, .spot-pc__play').forEach((n) => n.remove());
	g.classList.add('spot-player__ghost');
	Object.assign(g.style, px(rect), { borderRadius: radius, visibility: 'visible' });
	dialog.append(g);
	return g;
}

async function open(card) {
	if (busy) return;
	const media = card.querySelector('.spot-media');
	if (!media) return;
	busy = true;
	if (!dialog) build();
	source = media;

	dialog.style.setProperty('--cat', card.style.getPropertyValue('--cat'));
	const still = new Image();
	still.alt = '';
	still.src = card.dataset.spotStill || '';
	frame.replaceChildren(still);
	const tpl = card.querySelector('template.spot-strip-tpl');
	strip.replaceChildren(tpl ? tpl.content.cloneNode(true) : '');
	dialog.setAttribute('aria-label', card.dataset.spotTitle || 'Video');

	const from = media.getBoundingClientRect();
	const radius = getComputedStyle(media.closest('.spot-pc__in, .spot-tile') || media).borderRadius;
	dialog.showModal();

	if (reducedMotion()) {
		dialog.classList.add('is-lit');
	} else {
		const to = frame.getBoundingClientRect();
		frame.style.visibility = 'hidden';
		strip.style.opacity = '0';
		const ghost = ghostOf(media, from, radius);
		media.style.visibility = 'hidden'; // the picture has left the card…
		card.classList.add('is-away'); // …and takes the play badge with it
		requestAnimationFrame(() => dialog.classList.add('is-lit'));
		await ghost.animate([{ ...px(from), borderRadius: radius }, { ...px(to), borderRadius: '20px' }], {
			duration: 700,
			easing: EASE,
			fill: 'forwards',
		}).finished;
		frame.style.visibility = '';
		ghost.remove();
		strip.style.opacity = '';
		staggerIn([...strip.children]);
	}

	busy = false;
	if (!dialog.open) return; // closed during the flight: nothing to play into
	embed(frame, card.dataset.spotVideo, card.dataset.spotTitle);
	strip.querySelector('[data-spot-player-close]')?.focus({ preventScroll: true });
}

async function close() {
	if (busy || !dialog?.open) return;
	busy = true;
	unembed(frame);

	if (!reducedMotion()) {
		await Promise.all([...strip.children].map((el) => el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, fill: 'forwards' }).finished));
		const from = frame.getBoundingClientRect();
		const to = source.getBoundingClientRect(); // re-measured: the page may have scrolled underneath
		const radius = getComputedStyle(source.closest('.spot-pc__in, .spot-tile') || source).borderRadius;
		const ghost = ghostOf(frame, from, '20px');
		frame.style.visibility = 'hidden';
		dialog.classList.remove('is-lit');
		// Fly home if the card is on screen; otherwise let the picture fade away.
		const home = to.bottom > 0 && to.top < window.innerHeight;
		await (home
			? ghost.animate([{ ...px(from), borderRadius: '20px' }, { ...px(to), borderRadius: radius }], { duration: 600, easing: EASE, fill: 'forwards' })
			: ghost.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(.94)' }], { duration: 320, easing: 'ease-in', fill: 'forwards' })
		).finished;
		ghost.remove();
	}

	busy = false;
	dialog.close(); // returns focus to the card that opened it; reset() runs on the close event
}

/** Put everything back, however the dialog was closed. */
function reset() {
	unembed(frame);
	dialog.classList.remove('is-lit');
	dialog.querySelectorAll('.spot-player__ghost').forEach((g) => g.remove());
	dialog.getAnimations({ subtree: true }).forEach((a) => a.cancel());
	frame.style.visibility = '';
	strip.style.opacity = '';
	if (source) {
		source.style.visibility = '';
		source.closest('.spot-tile, .spot-pc')?.classList.remove('is-away');
	}
	source = null;
}

export function initCardPlayer() {
	if (!document.querySelector('.spot-tile[data-spot-video], .spot-pc[data-spot-video]')) return;
	if (typeof HTMLDialogElement === 'undefined') return; // links still work

	document.addEventListener('click', (e) => {
		const card = e.target.closest('.spot-tile[data-spot-video], .spot-pc[data-spot-video]');
		if (!card || !isPlainClick(e)) return;
		e.preventDefault();
		open(card);
	});
	initStillFallbacks();
}
